// Reading Form values: https://github.com/react-bootstrap/react-bootstrap/issues/2418
// Running a Background thread:  https://medium.com/@inkdrop/a-simple-way-to-run-js-in-background-thread-on-react-native-8fff345576da, https://www.newline.co/fullstack-react/articles/introduction-to-web-workers-with-react/

import React, { Component, useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup, Row, Col, Spinner, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styling/signup.css';

import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

import { Formik } from "formik";
import * as yup from 'yup';

import XenkiaAWSconfig from '../config'
import stripeConfig from '../stripe_config';

import StripeProcessing from '../helper_classes/stripe'
import HomeReturnLink from '../components/HomeReturnLink';
import AccountActivationPanel from '../components/AccountActivationPanel';
import { pricing_plan_list } from '../pricing_plans';

const YEARLY_DISCOUNT_PERCENTAGE = 30;
const pricingPlansByName = Object.fromEntries(
  pricing_plan_list.flat().map(plan => [plan.plan, plan])
);
const packageInfoQueries = pricing_plan_list.flatMap(plans => plans.flatMap(plan => (
  (plan.plan === 'Starter' ? ['monthly'] : ['monthly', 'yearly']).map(billingCycle => ({
    packageName: plan.plan.toLowerCase(),
    billingCycle
  }))
)));
const packageInfoKey = (packageName, billingCycle) => `${packageName.toLowerCase()}:${billingCycle.toLowerCase()}`;
const formatUsd = amount => Number(amount).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const cleanPackageFeature = feature => String(feature)
  .replace(/^(\s*(?:<[^>]*>\s*)*)[-–—]\s*/, '$1')
  .replace(/\s*\*{2,}\s*$/, '');
const SIGNUP_DRAFT_STORAGE_KEY = 'xenkia.signup.draft.v1';
const SIGNUP_DRAFT_MAX_AGE = 12 * 60 * 60 * 1000;
const SIGNUP_DRAFT_FIELDS = [
  'accountType', 'companyName', 'firstName', 'lastName', 'city', 'state',
  'zip', 'country', 'address', 'email', 'phoneNumber'
];

const readSignupDraft = () => {
  try {
    const stored = window.sessionStorage.getItem(SIGNUP_DRAFT_STORAGE_KEY);
    if (!stored) return null;
    const draft = JSON.parse(stored);
    if (!draft || !Number.isFinite(Number(draft.savedAt)) || Date.now() - Number(draft.savedAt) > SIGNUP_DRAFT_MAX_AGE) {
      window.sessionStorage.removeItem(SIGNUP_DRAFT_STORAGE_KEY);
      return null;
    }
    return draft;
  } catch (error) {
    return null;
  }
};

const saveSignupDraft = (values, signupPackage, signupStep) => {
  try {
    const safeValues = SIGNUP_DRAFT_FIELDS.reduce((result, field) => {
      result[field] = values?.[field] || '';
      return result;
    }, {});
    window.sessionStorage.setItem(SIGNUP_DRAFT_STORAGE_KEY, JSON.stringify({
      values: safeValues,
      signupPackage,
      signupStep,
      savedAt: Date.now()
    }));
  } catch (error) {
    // Signup still works when browser storage is unavailable.
  }
};

const clearSignupDraft = () => {
  try {
    window.sessionStorage.removeItem(SIGNUP_DRAFT_STORAGE_KEY);
  } catch (error) {
    // Ignore storage restrictions after a successful signup.
  }
};

const SignupDraftPersistence = ({ values, signupPackage, signupStep }) => {
  useEffect(() => {
    saveSignupDraft(values, signupPackage, signupStep);
  }, [values, signupPackage, signupStep]);
  return null;
};

  /**
   * All the User Details collection related logic will go here.
   * @returns 
   */
  const SignupForm = () => {

    const [restoredSignupDraft] = useState(readSignupDraft);
    const plansByAccountType = {
      Individual: ['Starter', 'Personal', 'Professional'],
      Company: ['Small Business', 'Medium Business', 'Large Business']
    };
    const signupParams = new URLSearchParams(window.location.search);
    const requestedAccountType = signupParams.get('accountType');
    const restoredPackage = restoredSignupDraft?.signupPackage || {};
    const defaultAccountType = ['Individual', 'Company'].includes(requestedAccountType)
      ? requestedAccountType
      : ['Individual', 'Company'].includes(restoredPackage.accountType) ? restoredPackage.accountType : 'Individual';
    const requestedPlan = signupParams.get('pricingPlan');
    const defaultPricingPlan = plansByAccountType[defaultAccountType].includes(requestedPlan)
      ? requestedPlan
      : plansByAccountType[defaultAccountType].includes(restoredPackage.pricingPlan)
        ? restoredPackage.pricingPlan
        : defaultAccountType === 'Company' ? 'Small Business' : 'Starter';
    const requestedCycle = signupParams.get('billingCycle');
    const defaultBillingCycle = ['Monthly', 'Yearly'].includes(requestedCycle)
      ? requestedCycle
      : ['Monthly', 'Yearly'].includes(restoredPackage.billingCycle) ? restoredPackage.billingCycle : 'Monthly';

    const [processPayment, setProcessPayment]                                       = useState(false);
    const [signupStep, setSignupStep]                                               = useState(() => restoredSignupDraft?.signupStep >= 2 ? 2 : 1);
    const [userInfo, setUserInfo]                                                   = useState(null);
    const [signupComplete, setSignupComplete]                                       = useState(false);
    const [paymentComplete, setPaymentComplete]                                     = useState(false);

    const [signupPackage, setSignupPackage] = useState(() => ({
      accountType: defaultAccountType,
      pricingPlan: defaultPricingPlan,
      billingCycle: defaultPricingPlan === 'Starter' && defaultBillingCycle === 'Yearly' ? 'Monthly' : defaultBillingCycle
    }));

    const [checkingIfEmailAlreadyRegistered, setCheckingIfEmailAlreadyRegistered]   = useState(false);
    const [emailAlreadyRegistered, setEmailAlreadyRegistered]                       = useState(false);
    const [emailCheckError, setEmailCheckError]                                 = useState('');
    const [signupSubmissionError, setSignupSubmissionError]                         = useState('');
    const [submittingSignup, setSubmittingSignup]                                   = useState(false);
    const [packageInfo, setPackageInfo] = useState({
      packageCost: 0,
      discount: 0,
      features: [],
      gettingPackageInfo: true,
      error: ''
    });
    const packageInfoCache = useRef({});
    const packageCatalogRequest = useRef(null);
    const [packageRetryCount, setPackageRetryCount] = useState(0);

    const fetchSinglePackageInfo = async query => {
      if (!XenkiaAWSconfig.packages.url) throw new Error('Plan details are unavailable in this environment.');
      const key = packageInfoKey(query.packageName, query.billingCycle);
      const response = await fetch(XenkiaAWSconfig.packages.url, {
        method: 'POST',
        body: JSON.stringify(query)
      });
      const data = await response.json();
      if (!response.ok || !data.body?.packageDetails) {
        throw new Error(data.body?.message || 'Plan details are temporarily unavailable.');
      }
      const details = data.body.packageDetails;
      const result = {
        packageCost: details.cost,
        discount: details.discount || 0,
        features: details.features || []
      };
      packageInfoCache.current[key] = result;
      return result;
    };

    const loadPackageCatalog = () => {
      if (packageCatalogRequest.current) return packageCatalogRequest.current;

      packageCatalogRequest.current = (async () => {
        try {
          const response = await fetch(XenkiaAWSconfig.packages.url, {
            method: 'POST',
            body: JSON.stringify({ packageQueries: packageInfoQueries })
          });
          const data = await response.json();
          const detailsByKey = data.body?.packageDetailsByKey;
          if (response.ok && detailsByKey && typeof detailsByKey === 'object') {
            Object.entries(detailsByKey).forEach(([key, details]) => {
              packageInfoCache.current[key.toLowerCase()] = {
                packageCost: details.cost,
                discount: details.discount || 0,
                features: details.features || []
              };
            });
          }
        } catch (error) {
          // Older deployments only support one package per request; use that API shape below.
        }

        const missingPackages = packageInfoQueries.filter(query => (
          !packageInfoCache.current[packageInfoKey(query.packageName, query.billingCycle)]
        ));
        await Promise.allSettled(missingPackages.map(fetchSinglePackageInfo));
        return packageInfoCache.current;
      })();

      return packageCatalogRequest.current;
    };

    useEffect(() => {
      const selectedKey = packageInfoKey(signupPackage.pricingPlan, signupPackage.billingCycle);
      let isCurrentSelection = true;
      if (packageRetryCount > 0) packageCatalogRequest.current = null;

      const cachedPackage = packageInfoCache.current[selectedKey];
      if (cachedPackage) {
        setPackageInfo({ ...cachedPackage, gettingPackageInfo: false, error: '' });
      } else {
        setPackageInfo(previous => ({ ...previous, gettingPackageInfo: true, error: '' }));
        loadPackageCatalog()
          .then(cache => {
            if (!isCurrentSelection) return;
            const details = cache[selectedKey];
            if (!details) throw new Error('Plan details are temporarily unavailable.');
            setPackageInfo({ ...details, gettingPackageInfo: false, error: '' });
          })
          .catch(error => {
            if (!isCurrentSelection) return;
            setPackageInfo({
              packageCost: 0,
              discount: 0,
              features: [],
              gettingPackageInfo: false,
              error: error.message || 'Plan details are temporarily unavailable.'
            });
          });
      }

      return () => { isCurrentSelection = false; };
    }, [signupPackage.pricingPlan, signupPackage.billingCycle, packageRetryCount]);


  //   /**
  //    * File selection for upload.
  //    * @param {*} e 
  //    */
  //    const selectProfilePicture = (e) => {

  //     // Disable the Profile Picture selection button.
  //     //setUploadButtonDisabled(true);

  //     // Get the file name selected by the user.
  //     const file = e.target.files[0];
  //     const file_extension = file.name.split('.').pop();

      

  //     s3Upload({
  //       bucketName:   'xenkiaassetbucket',
  //       objectName:   file.name,
  //       object:       file,
  //       subPath:      "profilePicture"    // Do not add any '/' as prefix or postfix.
  //     });

  //     // // Get the User Identity. Based on this we will generate the QR Code so that the App can know in which folder it can find the models.
  //     // Auth.currentUserInfo().then((userInfo) => {
  //     //     if (userInfo) 
  //     //     {
  //     //         user_id = encodeURIComponent(userInfo.id);
  //     //         setUserId(user_id);
  //     //     }
  //     // });

  //     // // Upload progress: https://stackoverflow.com/questions/55075608/how-do-i-send-progress-of-s3-upload-in-aws-amplify-react-native
  //     // Storage.put("profile_picture." + file_extension, file, {
  //     //     level: 'protected'})
  //     // .then( () => {  

  //     //         //setProfilePicture({...profilePicture, fileUrl: '', file:'', filename: ''});

  //     //         // Mark in the remote DB that the Profile Picture was successfully uploaded.
  //     //         updateProfilePictureUploadedDB();

  //     //         alert("Success: Profile picture uploaded!");

  //     //         // Enable the Profile Picture selection button.
  //     //         setUploadButtonDisabled(false);
  //     //         //UploadConfirmationToast();
  //     //     })
  //     // .catch(err=>{
  //     //     console.log('Error uploading file!', err)
  //     // });
  // }


    // This function will check if the Email that the new user have enter, is already registered or not.
    const createStarterAccount = async(values) => {
      setSubmittingSignup(true);
      setSignupSubmissionError('');
      try {
        if (!stripeConfig.stripe.apiUrl) {
          throw new Error('Signup is temporarily unavailable. Please try again later.');
        }
        const submittedUserInfo = { ...values };
        delete submittedUserInfo.accountType;
        const response = await fetch(stripeConfig.stripe.apiUrl, {
          method: 'POST',
          body: JSON.stringify({
            token: 'FAKE',
            userInfo: submittedUserInfo,
            accountInfo: signupPackage,
            discountToken: 'FAKE'
          })
        });
        const resultData = await response.json();
        const result = resultData.body || resultData;
        if (!response.ok || String(result.status || '').toLowerCase() !== 'success') {
          throw new Error(result.error || result.message || 'Registration could not be completed. Please try again.');
        }
        clearSignupDraft();
        setUserInfo(values);
        setSignupComplete(true);
      } catch (error) {
        setSignupSubmissionError(error.message || 'Registration could not be completed. Please try again.');
      } finally {
        setSubmittingSignup(false);
      }
    };

    const checkIfUserExists = async(email, values) => {
        if (!XenkiaAWSconfig.registration.checkIfUserREgistered || !stripeConfig.stripe.apiUrl) {
          setEmailCheckError('Signup is temporarily unavailable. Please try again later.');
          return;
        }
        setCheckingIfEmailAlreadyRegistered(true);
        setEmailCheckError('');
        setSignupSubmissionError('');
        setEmailAlreadyRegistered(false);
        try {
          const res = await fetch(XenkiaAWSconfig.registration.checkIfUserREgistered, {
            method: 'POST',
            body: JSON.stringify({ request_type: 'checkIfRegistered', email })
          });
          const response = await res.json();
          const message = response && response.body && response.body.message;

          if (!res.ok) throw new Error(message || 'We could not check that email. Please try again.');
          if (message === 'User does not exist!') {
            setUserInfo(values);
            if (signupPackage.pricingPlan === 'Starter') {
              await createStarterAccount(values);
            } else {
              saveSignupDraft(values, signupPackage, 3);
              setSignupStep(3);
              setProcessPayment(true);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          } else if (message === 'User exists!') {
            setEmailAlreadyRegistered(true);
            setEmailCheckError('This email is already registered. Try signing in or use another email.');
          } else {
            throw new Error(message || 'We could not verify that email. Please try again.');
          }
        } catch (error) {
          setEmailCheckError(error.message || 'We could not check that email. Please try again.');
        } finally {
          setCheckingIfEmailAlreadyRegistered(false);
        }
    }

    
    // Formik Schema.
    const schema = yup.object().shape(
    {
      accountType:          yup.string()
                            .oneOf(['Individual', 'Company'])
                            .required('Choose an account type'),
      companyName:          yup.string()
                            .when('accountType', {
                              is: 'Company',
                              then: yup.string().trim().required('Company name is required'),
                              otherwise: yup.string().nullable()
                            }),
      firstName:            yup.string()
                            .required('Required'),
      lastName:             yup.string()
                            .when('accountType', {
                              is: 'Company',
                              then: yup.string().required('Contact last name is required'),
                              otherwise: yup.string().nullable()
                            }),
      // username:             yup.string()
      //                       .test('no-space','Username cannot contain any <space>', async (value) => {
      //                           if(value) return (value.search(" ") < 0);
      //                           else return true;
      //                         })
      //                       .min(5, 'Must be longer than 4 characters!')
      //                       .max(30, 'Must be shorter than 30 characters!')
      //                       .required('Required'),
      city:                 yup.string()
                            .required('Required'),
      state:                yup.string()
                            .nullable(),
      zip:                  yup.string()
                            .required('Required'),
      country:              yup.string()
                            .required('Required'),
      address:              yup.string()
                            .required('Required'),
      email:                yup.string()
                            .email()
                            .required('Required'),
      password:             yup.string()
                            .min(8, 'Use at least 8 characters')
                            .matches(/[A-Z]/, 'Add at least one capital letter')
                            .matches(/[^A-Za-z0-9\s]/, 'Add at least one special character')
                            .required('Required'),
      phoneNumber:          yup.string()
                            .required('Required')
      //file:       yup.mixed().required(),
      //terms:      yup.bool().required().oneOf([true], 'Terms must be accepted')
    });

    const yearlyDiscountPercentage = signupPackage.billingCycle === 'Yearly' && signupPackage.pricingPlan !== 'Starter'
      ? Number(packageInfo.discount) || 0
      : 0;
    const packageCostNumber = Number(packageInfo.packageCost);
    const discountedPackageCost = Number.isFinite(packageCostNumber)
      ? (packageCostNumber * (1 - yearlyDiscountPercentage / 100)).toFixed(2)
      : '';
    const signupProgressSteps = signupPackage.pricingPlan === 'Starter'
      ? ['Plan', 'Your details']
      : ['Plan', 'Your details', 'Payment'];
    const activeSignupStep = processPayment ? 3 : signupStep;
    const restoredValues = restoredSignupDraft?.values || {};
    const initialSignupValues = userInfo || {
      ...restoredValues,
      accountType: signupPackage.accountType,
      companyName: restoredSignupDraft?.signupPackage?.accountType === signupPackage.accountType
        ? restoredValues.companyName || ''
        : '',
      firstName: restoredValues.firstName || '',
      lastName: restoredValues.lastName || '',
      city: restoredValues.city || '',
      state: restoredValues.state || '',
      zip: restoredValues.zip || '',
      country: restoredValues.country || '',
      address: restoredValues.address || '',
      password: '',
      confirmPassword: '',
      email: restoredValues.email || '',
      confirmEmail: restoredValues.email || '',
      phoneNumber: restoredValues.phoneNumber || ''
    };


    

    return (
      <main className={`signup-page${paymentComplete || signupComplete ? ' signup-payment-complete' : ''}`}>
        <div className="signup-shell">
          {!paymentComplete && !signupComplete && <header className="signup-intro">
            <div className="signup-topbar">
              <div className="signup-topbar-home-link"><HomeReturnLink /></div>
            </div>
            <div className="signup-secure-note">Secure account setup</div>

            <p className="signup-eyebrow">XENKIA ACCOUNT</p>
            <h1>Create your account</h1>
            <p>Set up your plan and account in a few simple steps.</p>
            {!signupComplete && (
              <ol className="signup-progress" aria-label={`Step ${activeSignupStep} of ${signupProgressSteps.length}`}>
                {signupProgressSteps.map((stepName, index) => {
                  const stepNumber = index + 1;
                  const stepClass = activeSignupStep === stepNumber
                    ? 'is-active'
                    : activeSignupStep > stepNumber ? 'is-complete' : '';
                  return (
                    <React.Fragment key={stepName}>
                      {index > 0 && <li className="signup-progress-line" aria-hidden="true" />}
                      <li className={`signup-progress-step ${stepClass}`} aria-current={activeSignupStep === stepNumber ? 'step' : undefined}>
                        <b>{activeSignupStep > stepNumber ? '✓' : stepNumber}</b>
                        <span>{stepName}</span>
                      </li>
                    </React.Fragment>
                  );
                })}
              </ol>
            )}
          </header>}
          <section className={`signup-card${paymentComplete || signupComplete ? ' signup-success-card' : ''}`}>
          {signupComplete
          ?
              <AccountActivationPanel email={userInfo?.email} eyebrow="ACCOUNT CREATED" title="Activate your account" subtitle="Your details are set. Check your email to finish activating your account." />
          : processPayment
          ? 
              <div>
                    <StripeProcessing
                      processPayment    =   {processPayment}
                      userInfo          =   {userInfo}
                      initialPackage    =   {signupPackage}
                      packageInfo       =   {packageInfo}
                      onPaymentSuccess =   {() => { clearSignupDraft(); setPaymentComplete(true); }}
                      onBack            =   {() => {
                        setProcessPayment(false);
                        setSignupStep(2);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
              </div>
          :
              <Formik
                validationSchema={schema}
                enableReinitialize

                onSubmit={(values) => {
                  const { companyName, ...sharedValues } = values;
                  const registrationValues = {
                    ...sharedValues,
                    ...(values.accountType === 'Company' ? { companyName: (companyName || '').trim() } : {}),
                    confirmEmail: values.email,
                    confirmPassword: values.password
                  };
                  setUserInfo(registrationValues);
                  checkIfUserExists(values.email, registrationValues);
                }}
                
                initialValues={initialSignupValues}
              >
                {({
                  handleSubmit, handleChange, values, isValid, errors, setFieldValue
                }) => (
                    <>
                    <SignupDraftPersistence
                      values={values}
                      signupPackage={signupPackage}
                      signupStep={signupStep}
                    />
                    <Form noValidate onSubmit={event => {
                      if (signupStep === 1) {
                        event.preventDefault();
                        return;
                      }
                      handleSubmit(event);
                    }}>
                      {/* Temporarily disabled; keep this test helper for local signup checks.
                      {signupStep === 2 && process.env.NODE_ENV === 'development' && (
                        <div className="signup-test-tools">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              const testEmail = `signup-test-${Date.now()}@example.com`;
                              setEmailCheckError('');
                              setEmailAlreadyRegistered(false);
                              setSignupSubmissionError('');
                              setValues({
                                firstName: 'Test',
                                lastName: 'User',
                                accountType: signupPackage.accountType,
                                companyName: signupPackage.accountType === 'Company' ? 'Example Test Studio' : '',
                                address: '123 Test Lane',
                                city: 'Testville',
                                state: 'CA',
                                zip: '90210',
                                country: 'United States',
                                phoneNumber: '12025550100',
                                email: testEmail,
                                confirmEmail: testEmail,
                                password: 'TestSignup!2026',
                                confirmPassword: 'TestSignup!2026'
                              });
                            }}
                          >
                            Fill test details
                          </Button>
                          <span>Development only · does not submit</span>
                        </div>
                      )}
                      */}
                      {signupStep === 1 ? (
                        <section className="signup-plan-step" aria-labelledby="signup-plan-step-title">
                          <div className="signup-stage-heading">
                            <div>
                              <p className="signup-stage-eyebrow">STEP 1 · PLAN</p>
                              <h2 id="signup-plan-step-title">Choose your plan</h2>
                              <p>Choose the account that best fits you, then pick a plan.</p>
                            </div>
                          </div>

                          <fieldset className="signup-choice-fieldset">
                            <legend>Account type</legend>
                            <div className="signup-account-options" role="group" aria-label="Choose an account type">
                              {['Individual', 'Company'].map(accountType => (
                                <button
                                  key={accountType}
                                  type="button"
                                  className={'signup-account-option' + (signupPackage.accountType === accountType ? ' is-selected' : '')}
                                  aria-pressed={signupPackage.accountType === accountType}
                                  onClick={() => {
                                    setFieldValue('accountType', accountType);
                                    setFieldValue('companyName', '');
                                    setSignupPackage(previous => {
                                      const pricingPlan = plansByAccountType[accountType].includes(previous.pricingPlan)
                                        ? previous.pricingPlan
                                        : accountType === 'Company' ? 'Small Business' : 'Starter';
                                      return {
                                        ...previous,
                                        accountType,
                                        pricingPlan,
                                        billingCycle: pricingPlan === 'Starter' ? 'Monthly' : previous.billingCycle
                                      };
                                    });
                                  }}
                                >
                                  <span className="signup-account-option-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" focusable="false">
                                      {accountType === 'Individual' ? (
                                        <>
                                          <circle cx="12" cy="8" r="3.3" />
                                          <path d="M5.5 20c.7-3.4 3.1-5.3 6.5-5.3s5.8 1.9 6.5 5.3" />
                                        </>
                                      ) : (
                                        <>
                                          <path d="M4 20V5.5h10V20M14 10h6v10M7.5 9h3M7.5 12.5h3M7.5 16h3M16.5 13.5h1M16.5 17h1M2.5 20h19" />
                                        </>
                                      )}
                                    </svg>
                                  </span>
                                  <span className="signup-account-option-copy">
                                    <strong>{accountType === 'Company' ? 'Enterprise' : accountType}</strong>
                                  </span>
                                  <span className="signup-choice-check" aria-hidden="true">✓</span>
                                </button>
                              ))}
                            </div>
                          </fieldset>

                          <section className="signup-plan-selector" aria-labelledby="signup-plan-selector-title">
                            <div className="signup-plan-selector-heading">
                              <div>
                                <h3 id="signup-plan-selector-title">Select a plan</h3>
                              </div>
                            </div>

                            <div className="signup-plan-groups">
                              {[
                                { label: 'Individual plans', accountType: 'Individual', plans: plansByAccountType.Individual },
                                { label: 'Business plans', accountType: 'Company', plans: plansByAccountType.Company }
                              ].filter(group => group.accountType === signupPackage.accountType).map(group => (
                                <div className="signup-plan-group" key={group.label}>
                                  <div className="signup-plan-options" role="group" aria-label={group.label}>
                                    {group.plans.map(plan => (
                                      <button
                                        key={plan}
                                        type="button"
                                        className={'signup-plan-option' + (signupPackage.pricingPlan === plan ? ' is-selected' : '')}
                                        aria-pressed={signupPackage.pricingPlan === plan}
                                        onClick={() => setSignupPackage(previous => ({
                                          ...previous,
                                          pricingPlan: plan,
                                          billingCycle: plan === 'Starter' ? 'Monthly' : previous.billingCycle
                                        }))}
                                      >
                                        <span className="signup-plan-option-topline">
                                          <span className="signup-choice-check" aria-hidden="true">✓</span>
                                        </span>
                                        <strong>{plan}</strong>
                                        <span className="signup-plan-option-price">
                                          {plan === 'Starter'
                                            ? 'Free'
                                            : signupPackage.billingCycle === 'Yearly'
                                              ? <>
                                                  <s>${formatUsd(Number(pricingPlansByName[plan].monthly_cost_in_usd) * 12)}</s>
                                                  <strong>${formatUsd(Number(pricingPlansByName[plan].monthly_cost_in_usd) * 12 * (1 - YEARLY_DISCOUNT_PERCENTAGE / 100))} / yr</strong>
                                                </>
                                              : `$${formatUsd(pricingPlansByName[plan].monthly_cost_in_usd)} / mo`}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div
                              className={'signup-billing-selector' + (signupPackage.pricingPlan === 'Starter' ? ' is-disabled' : '')}
                              role="group"
                              aria-label="Choose a billing period"
                            >
                              <span className="signup-billing-label">Billing period</span>
                              <div className="signup-billing-options">
                                <button
                                  type="button"
                                  className={'signup-billing-option' + (signupPackage.billingCycle === 'Monthly' ? ' is-active' : '')}
                                  aria-pressed={signupPackage.billingCycle === 'Monthly'}
                                  disabled={signupPackage.pricingPlan === 'Starter'}
                                  title={signupPackage.pricingPlan === 'Starter' ? 'Billing period selection is unavailable for Starter' : undefined}
                                  onClick={() => setSignupPackage(previous => ({ ...previous, billingCycle: 'Monthly' }))}
                                >Monthly</button>
                                <button
                                  type="button"
                                  className={'signup-billing-option' + (signupPackage.billingCycle === 'Yearly' ? ' is-active' : '')}
                                  aria-pressed={signupPackage.billingCycle === 'Yearly'}
                                  disabled={signupPackage.pricingPlan === 'Starter'}
                                  title={signupPackage.pricingPlan === 'Starter' ? 'Yearly billing is unavailable for Starter' : undefined}
                                  onClick={() => setSignupPackage(previous => ({ ...previous, billingCycle: 'Yearly' }))}
                                >Yearly <span>Save {YEARLY_DISCOUNT_PERCENTAGE}%</span></button>
                              </div>
                            </div>
                          </section>
                          <section className="signup-plan-summary" aria-live="polite" aria-label="Selected plan details">
                            <div className="signup-selected-plan-heading">
                              <div className="signup-selected-plan-copy">
                                <h3>{signupPackage.pricingPlan}</h3>
                                <div className="signup-plan-badges">
                                  <span>{signupPackage.accountType}</span>
                                  {signupPackage.pricingPlan !== 'Starter' && <span>{signupPackage.billingCycle} billing</span>}
                                </div>
                              </div>
                              <div className="signup-plan-price">
                                {packageInfo.gettingPackageInfo
                                  ? <Spinner animation="border" size="sm" aria-label="Loading plan price" />
                                  : signupPackage.pricingPlan === 'Starter'
                                    ? <span className="signup-current-price">Free</span>
                                    : packageInfo.packageCost
                                      ? <>
                                          {yearlyDiscountPercentage > 0 && <span className="signup-original-price">${packageInfo.packageCost}</span>}
                                          <span className="signup-current-price">${yearlyDiscountPercentage > 0 ? discountedPackageCost : packageInfo.packageCost}</span>
                                          <span className="signup-plan-price-period">/ {signupPackage.billingCycle === 'Monthly' ? 'month' : 'year'}</span>
                                        </>
                                      : 'Price unavailable'}
                              </div>
                            </div>
                            {packageInfo.error && (
                              <Alert variant="warning" className="signup-plan-alert">
                                <span>{packageInfo.error}</span>
                                <Button variant="link" className="signup-plan-retry" type="button" onClick={() => setPackageRetryCount(count => count + 1)}>
                                  Try again
                                </Button>
                              </Alert>
                            )}
                            {!packageInfo.gettingPackageInfo && !packageInfo.error && Object.keys(packageInfo.features || {}).length > 0 && (
                              <ul className="signup-plan-features">
                                {[8, 9, 7, 0, 4, 1, 6, 3, 2, 5]
                                  .filter(index => packageInfo.features[index])
                                  .map(index => <li key={index} dangerouslySetInnerHTML={{ __html: cleanPackageFeature(packageInfo.features[index]) }} />)}
                              </ul>
                            )}
                            {signupPackage.pricingPlan === 'Starter' && (
                              <p className="signup-plan-reset-note">* Starter view allowances automatically reset each month for free.</p>
                            )}
                          </section>

                          <div className="signup-step-actions signup-plan-actions">
                            <p className="signup-review-note"><span><b>Note:</b> Your choice can be reviewed before payment.</span></p>
                            <Button
                              className="signup-next-button"
                              type="button"
                              disabled={packageInfo.gettingPackageInfo || Boolean(packageInfo.error)}
                              onClick={() => {
                                setSignupStep(2);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                            >
                              Continue to your details <span aria-hidden="true">→</span>
                            </Button>
                          </div>
                        </section>
                      ) : (
                        <div className="signup-details-step">
                          <div className="signup-stage-heading">
                            <Button
                              className="signup-back-button signup-flow-return"
                              type="button"
                              onClick={() => {
                                setSignupStep(1);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              }}
                              aria-label="Back to plan selection"
                              title="Back to plan selection"
                            >
                              <svg className="signup-return-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                <path d="M19 7v4H5.83l3.58-3.59L8 6l-6 6 6 6 1.41-1.41L5.83 13H21V7z" />
                              </svg>
                            </Button>
                            <div>
                              <p className="signup-stage-eyebrow">STEP 2 · YOUR DETAILS</p>
                              <h2>Tell us about yourself</h2>
                              <p>{signupPackage.accountType === 'Company' ? 'Add your business contact and sign-in details.' : 'Add your contact and sign-in details.'}</p>
                            </div>
                          </div>
                          {restoredSignupDraft ? (
                            <p className="signup-draft-note" role="status">
                              Your plan and details were restored for this tab. For your security, enter your password again; passwords are never saved.
                            </p>
                          ) : (
                            <p className="signup-draft-note">Your plan and contact details are saved in this tab. Passwords are never saved.</p>
                          )}
                          <h3 className="signup-fields-heading">Contact information</h3>
                          <p className="signup-section-description">We’ll use these details to set up and support your account.</p>
                      {values.accountType === 'Company' && (
                        <Row className="g-3 mb-2 signup-details-form-row">
                          <Form.Group as={Col} controlId="signup-company-name" className="position-relative">
                            <Form.Label>Company name</Form.Label>
                            <Form.Control
                              type="text"
                              name="companyName"
                              autoComplete="organization"
                              placeholder="Your company or organization"
                              value={values.companyName || ''}
                              onChange={handleChange}
                              isInvalid={errors.companyName}
                            />
                            <Form.Control.Feedback type="invalid" tooltip>
                              {errors.companyName}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                      )}

                      {/* {console.log(values.accountType)} */}
                      {/* {console.log(defaultAccountType, defaultPricingPlan, defaultBillingCycle)} */}

                      {/* <Row className="mb-3">
                          <Image style={{marginLeft: 'auto', marginRight: 'auto'}} src={individual} height='96px' alt="picture" rounded />
                      </Row>


                      <Row className="g-3 mb-2 signup-details-form-row">
                        <Form.Group className="position-relative mb-3" style={{marginLeft: 'auto', marginRight: 'auto'}}>
                          <Form.Control
                            type="file"
                            required
                            name="file"
                            //onChange={handleChange}
                            accept=".jpg"
                            onChange={selectProfilePicture}
                            isInvalid={errors.file}
                          />
                          <Form.Control.Feedback type="invalid" tooltip>
                            {errors.file}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>

                      <br/> */}

                      <Row className="g-3 mb-2 signup-details-form-row">
                        <Form.Group as={Col} xs="6" md="6" controlId="validationFormik101" className="position-relative"
                        >
                          <Form.Label>{values.accountType === 'Company' ? 'Contact first name' : 'First name'}</Form.Label>
                          <Form.Control
                            type="text"
                            name="firstName"
                            autoComplete="given-name"
                            placeholder="John / Jane"
                            value={values.firstName}
                            onChange={handleChange}
                            isInvalid={errors.firstName}
                          />
                          <Form.Control.Feedback type="invalid" tooltip>
                            {errors.firstName}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group as={Col} xs="6" md="6" controlId="validationFormik102" className="position-relative"
                        >
                          <Form.Label>{values.accountType === 'Company' ? 'Contact last name' : 'Last name'}</Form.Label>
                          <Form.Control
                            type="text"
                            name="lastName"
                            autoComplete="family-name"
                            placeholder="Doe"
                            value={values.lastName}
                            onChange={handleChange}
                            isInvalid={errors.lastName}
                          />
                          <Form.Control.Feedback type="invalid" tooltip>
                            {errors.lastName}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>

                      <Row className="g-3 mb-2 signup-details-form-row">
                        <Form.Group
                            as={Col}
                            //md="3"
                            controlId="validationFormikAddress"
                            className="position-relative"
                          >
                            <Form.Label>{values.accountType === 'Company' ? 'Business address' : 'Street address'}</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="12345 Street, Building, Floor, Suit"
                              name="address"
                              autoComplete="street-address"
                              value={values.address}
                              onChange={handleChange}
                              isInvalid={errors.address}
                            />
              
                            <Form.Control.Feedback type="invalid" tooltip>
                              {errors.address}
                            </Form.Control.Feedback>
                        </Form.Group>
                      </Row>

                      <Row className="g-3 mb-2 signup-details-form-row">
                        <Form.Group
                          as={Col}
                          xs="6"
                          md="3"
                          controlId="validationFormikCity"
                          className="position-relative"
                        >
                          <Form.Label>City</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="New York"
                            name="city"
                              autoComplete="address-level2"
                            value={values.city}
                            onChange={handleChange}
                            isInvalid={errors.city}
                          />
            
                          <Form.Control.Feedback type="invalid" tooltip>
                            {errors.city}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          xs="6"
                          md="3"
                          controlId="validationFormik104"
                          className="position-relative"
                        >
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="New York"
                            name="state"
                            autoComplete="address-level1"
                            value={values.state}
                            onChange={handleChange}
                          />
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          xs="6"
                          md="3"
                          controlId="validationFormik105"
                          className="position-relative"
                        >
                          <Form.Label>Zip/Postal Code</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="10001"
                            name="zip"
                              autoComplete="postal-code"
                            value={values.zip}
                            onChange={handleChange}
                            isInvalid={errors.zip}
                          />
            
                          <Form.Control.Feedback type="invalid" tooltip>
                            {errors.zip}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          as={Col}
                          xs="6"
                          md="3"
                          controlId="validationFormik106"
                          className="position-relative"
                        >
                          <Form.Label>Country</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="USA"
                            name="country"
                            autoComplete="country-name"
                            value={values.country}
                            onChange={handleChange}
                            isInvalid={errors.country}
                          />
            
                          <Form.Control.Feedback type="invalid" tooltip>
                            {errors.country}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Row>

                      <Row className="g-3 mb-2 signup-details-form-row">
                        <Form.Group as={Col} md="6" controlId="validationFormikPhoneNumber" className="position-relative">
                            <Form.Label>{values.accountType === 'Company' ? 'Business phone' : 'Phone number'}</Form.Label>
                            <InputGroup hasValidation>
                                <PhoneInput
                                    name='phone'
                                    required= {true}
                                    country='us'
                                    placeholder='+1202-555-0932'
                                    value={values.phoneNumber}
                                    /* onChange={handleChange} */
                                    onChange={phone => setFieldValue('phoneNumber', phone)}
                                    /* onChange={phone => this.setState({ phone })} */
                                    isValid={(value, country) => {
                                      if(value.length > 1) return true;
                                      else return false;
                                    }}
                                />
                            </InputGroup>
                            <Form.Control.Feedback type="invalid" tooltip>
                              {errors.phoneNumber}
                            </Form.Control.Feedback>
                        </Form.Group>
                      </Row>


                      <br/>
                      <h3 className="signup-fields-heading signup-signin-heading">Sign-in details</h3>

                        <Row className="g-3 mb-2 signup-details-form-row">
                          {/* <Form.Group as={Col} md="6" controlId="validationFormikUsername2">
                            <Form.Label>Username</Form.Label>
                            <InputGroup hasValidation>
                              <InputGroup.Text id="inputGroupPrepend">@</InputGroup.Text>
                              <Form.Control
                                type="text"
                                placeholder="myusername"
                                aria-describedby="inputGroupPrepend"
                                name="username"
                                value={values.username}
                                onChange={handleChange}
                                isInvalid={errors.username}
                              />
                              <Form.Control.Feedback type="invalid" tooltip>
                                {errors.username}
                              </Form.Control.Feedback>
                            </InputGroup>
                          </Form.Group> */}

                          <Form.Group
                            as={Col}
                            md="6"
                            controlId="validationFormikEmail"
                            className="position-relative"
                          >
                            <Form.Label>{values.accountType === 'Company' ? 'Work email' : 'Email'}</Form.Label>
                            <Form.Control
                              type="email"
                              placeholder="your@email.address"
                              name="email"
                              autoComplete="email"
                              value={values.email}
                              onChange={event => {
                                handleChange(event);
                                setEmailCheckError('');
                              }}
                              isInvalid={errors.email}
                            />
              
                            <Form.Control.Feedback type="invalid" tooltip>
                              {errors.email}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group
                              as={Col}
                              md="6"
                              controlId="validationFormikPassword"
                              className="position-relative"
                            >
                              <Form.Label>Password</Form.Label>
                              <Form.Control
                                type="password"
                                placeholder="Create a strong password"
                                name="password"
                                autoComplete="new-password"
                                value={values.password}
                                onChange={handleChange}
                                isInvalid={errors.password}
                              />
                
                              <Form.Control.Feedback type="invalid" tooltip>
                                {errors.password}
                              </Form.Control.Feedback>
                              <Form.Text className="text-muted">At least 8 characters, one capital letter, and one special character.</Form.Text>
                            </Form.Group>
                        </Row>

                        {signupSubmissionError && (
                          <Alert className="signup-form-alert" variant="danger" role="alert">
                            {signupSubmissionError}
                          </Alert>
                        )}

                        {emailCheckError && (
                          <Alert className="signup-form-alert" variant={emailAlreadyRegistered ? 'warning' : 'danger'} role="alert">
                            {emailCheckError}
                          </Alert>
                        )}

                        <div className="signup-step-actions signup-details-actions">
                          <p className="signup-review-note">{signupPackage.pricingPlan === 'Starter' ? <span><b>Note:</b> No payment is needed for the Starter plan.</span> : <span><b>Note:</b> Your plan and payment details are reviewed before checkout. </span>}</p>
                          
                          <Button
                            className="signup-next-button"
                            type="submit"
                            variant="success"
                            disabled={!isValid || checkingIfEmailAlreadyRegistered || submittingSignup || packageInfo.gettingPackageInfo || Boolean(packageInfo.error)}
                          >
                            {submittingSignup
                              ? <><Spinner size="sm" animation="border" className="me-2" /> Creating account...</>
                              : checkingIfEmailAlreadyRegistered
                                ? <><Spinner size="sm" animation="border" className="me-2" /> Checking email...</>
                                : packageInfo.gettingPackageInfo
                                  ? <><Spinner size="sm" animation="border" className="me-2" /> Loading plan...</>
                                  : <><b>{signupPackage.pricingPlan === 'Starter' ? 'Create free account' : 'Continue to payment'}</b><span aria-hidden="true"> →</span></>
                            }
                          </Button>
                        </div>
                      </div>
                      )}
                    </Form>
                    </>
                )}
              </Formik>
          }

          </section>
        </div>
      </main>
    );
  }




class Signup extends Component {

    render() {
        return (
            <div>
                <SignupForm />
            </div>
        );
    }    
}


export default Signup;
